import React from 'react';
import { useSiteImages } from '../context/SiteImagesContext';
import { Logo } from '../components/Logo';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { ShieldCheck, Leaf, Sparkles, CheckCircle2, Award, HeartHandshake, ArrowRight } from 'lucide-react';

interface AboutPageProps {
  onNavigate: (page: string) => void;
}

export const AboutPage: React.FC<AboutPageProps> = ({ onNavigate }) => {
  const { images } = useSiteImages();
  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb="About Us"
        parentPage={{ label: 'Home', page: 'home' }}
        badgeIcon={<ShieldCheck className="w-3.5 h-3.5" />}
        badgeText="Fabricated & Calibrated in Nairobi, Kenya"
        badgeColor="emerald"
        title="Where Innovation Meets Reliability"
        description="Welcome to Saints Tech Solutions, where precision engineering meets commercial durability in providing essential everyday equipment: Water Purification Systems, Milk ATMs, Cooking Oil ATMs, and Commercial Pasteurizers."
        backgroundImage={images.aboutBanner}
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
        {/* Brand Logo Presentation */}
        <div className="text-center max-w-xl mx-auto">
          <div className="inline-block p-4 bg-white border border-slate-200 rounded-2xl shadow-xs">
            <Logo size="lg" variant="full" />
          </div>
        </div>

      {/* Mission & Vision Split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Mission Card */}
        <div className="bg-sky-50/70 border border-sky-200/80 rounded-3xl p-8 sm:p-10 space-y-4">
          <div className="w-12 h-12 bg-sky-600 text-white rounded-2xl flex items-center justify-center font-bold text-lg shadow-sm">
            🎯
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-sky-700">
            Purpose & Commitment
          </span>
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
            Our Mission
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            At Saints Tech Solution, our mission is to deliver top-notch, affordable, and sustainable solutions that meet the diverse needs of our customers. We aim to enhance health and convenience by ensuring access to clean water, fresh salads, and dairy products through our innovative vending machines.
          </p>
        </div>

        {/* Vision Card */}
        <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-10 space-y-4 border border-slate-800">
          <div className="w-12 h-12 bg-emerald-500 text-slate-950 rounded-2xl flex items-center justify-center font-bold text-lg shadow-sm">
            🌍
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
            Global Leadership
          </span>
          <h2 className="text-2xl font-extrabold text-white tracking-tight">
            Our Vision
          </h2>
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            We envision a world where everyone has access to clean water and fresh, nutritious food at their fingertips. Through our advanced technology and dedicated service, we strive to be a global leader in the vending and purification industries, setting new standards for quality and innovation.
          </p>
        </div>
      </div>

      {/* Core Values: Innovation, Sustainability, Quality */}
      <div className="space-y-6">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
            The Saints Tech Advantage
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Why Choose Saints Tech Solution?
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Built on robust electromechanical standards and unwavering support for African businesses.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-7 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
            <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Innovation</h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              We leverage the latest technologies to develop products that are not only efficient but also ahead of the curve in terms of functionality and design. From digital GSM telemetry to automated CIP sanitation cycles, we build future-ready equipment.
            </p>
          </div>

          <div className="bg-white p-7 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Leaf className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Sustainability</h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              We are dedicated to creating solutions that are environmentally friendly and sustainable. Our vending and refill models cut packaging waste drastically, while efficient components minimize power consumption.
            </p>
          </div>

          <div className="bg-white p-7 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Uncompromising Quality</h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              Our products undergo rigorous testing to ensure they meet the highest standards of quality and reliability. Every machine is calibrated, pressure tested, and sanitized before customer handover.
            </p>
          </div>
        </div>
      </div>

      {/* In-Depth Sustainability Pledge */}
      <div className="bg-emerald-950 text-white rounded-3xl p-8 sm:p-12 border border-emerald-900 space-y-6">
        <div className="max-w-3xl space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-semibold">
            <Leaf className="w-3.5 h-3.5" />
            <span>Environmental Stewardship</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Our Commitment to Sustainability
          </h2>
          <p className="text-sm sm:text-base text-emerald-100 leading-relaxed">
            Saints Tech Solution is committed to sustainability and environmental responsibility. Our products are designed to minimize waste, reduce energy consumption, and promote eco-friendly practices. We believe that technology should not only serve people but also protect the planet. By leveraging innovative green engineering, we deliver scalable solutions for a cleaner future.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-emerald-900/80">
          <div className="space-y-1.5">
            <h4 className="text-sm font-bold text-white">Zero Single-Use Waste</h4>
            <p className="text-xs text-emerald-200/80 leading-relaxed">
              Our refill stations encourage customers to bring reusable bottles and jugs, eliminating tons of plastic waste annually across Kenyan communities.
            </p>
          </div>
          <div className="space-y-1.5">
            <h4 className="text-sm font-bold text-white">High Thermal Efficiency</h4>
            <p className="text-xs text-emerald-200/80 leading-relaxed">
              Our pasteurizers feature double-wall polyurethane insulation that traps heat, cutting electrical energy expenditure by up to 35%.
            </p>
          </div>
          <div className="space-y-1.5">
            <h4 className="text-sm font-bold text-white">Responsible Water Recovery</h4>
            <p className="text-xs text-emerald-200/80 leading-relaxed">
              Our reverse osmosis skid plants are engineered with concentrate recirculation loops to optimize recovery rates and minimize rejected water.
            </p>
          </div>
        </div>
      </div>

      {/* Facilities & Location Callout */}
      <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 max-w-xl">
          <h3 className="text-xl font-bold text-slate-900">
            Engineered at Juja Road Green Mall, Nairobi
          </h3>
          <p className="text-xs sm:text-sm text-slate-600">
            Our manufacturing and assembly workshop welcomes business owners, dairy farmers, and water station operators for walk-in consultations, technical demonstrations, and testing.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onNavigate('contact')}
            className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
          >
            Visit Our Nairobi Office
          </button>
          <button
            onClick={() => onNavigate('products')}
            className="px-5 py-2.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
          >
            View Machine Catalog
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};
